// ============ CONSTANTS ============

// Deal stages - matches pipeline workflow
export const STAGES = [
  { id: 'lead', name: 'Lead', color: 'bg-neutral-600', textColor: 'text-neutral-400' },
  { id: 'negotiating', name: 'Negotiating', color: 'bg-amber-600', textColor: 'text-amber-400' },
  { id: 'contracted', name: 'Contracted', color: 'bg-sky-600', textColor: 'text-sky-400' },
  { id: 'in-progress', name: 'In Progress', color: 'bg-violet-600', textColor: 'text-violet-400' },
  { id: 'delivered', name: 'Delivered', color: 'bg-emerald-600', textColor: 'text-emerald-400' },
  { id: 'invoiced', name: 'Invoiced', color: 'bg-cyan-600', textColor: 'text-cyan-400' },
  { id: 'paid', name: 'Paid', color: 'bg-lime-400', textColor: 'text-lime-400' },
];

// Platforms
export const PLATFORMS = [
  { id: 'youtube', name: 'YouTube', color: 'text-red-500' },
  { id: 'twitch', name: 'Twitch', color: 'text-purple-500' },
  { id: 'tiktok', name: 'TikTok', color: 'text-pink-500' },
  { id: 'instagram', name: 'Instagram', color: 'text-orange-500' },
  { id: 'twitter', name: 'Twitter/X', color: 'text-sky-500' },
  { id: 'podcast', name: 'Podcast', color: 'text-green-500' },
  { id: 'newsletter', name: 'Newsletter', color: 'text-blue-500' },
];

// Deal types
export const DEAL_TYPES = [
  'Stream Integration',
  'Dedicated Video', 
  'Social Post',
  'Story/Reel',
  'Product Review',
  'Affiliate',
  'Brand Ambassador',
  'Event Appearance',
  'Podcast Mention',
];

// Industries
export const INDUSTRIES = [
  'Gaming',
  'Tech',
  'Food & Beverage',
  'Fashion',
  'Finance',
  'Health & Fitness',
  'Education',
  'Entertainment',
  'Lifestyle',
  'Other',
];

// Follower ranges for marketplace
export const FOLLOWER_RANGES = [
  'Any',
  '1K-10K',
  '10K-50K',
  '50K-100K',
  '100K-500K',
  '500K-1M',
  '1M+',
];

// Invoice status
export const INVOICE_STATUS = [
  { id: 'draft', name: 'Draft', color: 'bg-neutral-600', textColor: 'text-neutral-400' },
  { id: 'sent', name: 'Sent', color: 'bg-sky-600', textColor: 'text-sky-400' },
  { id: 'viewed', name: 'Viewed', color: 'bg-violet-600', textColor: 'text-violet-400' },
  { id: 'paid', name: 'Paid', color: 'bg-lime-400', textColor: 'text-lime-400' },
  { id: 'overdue', name: 'Overdue', color: 'bg-red-600', textColor: 'text-red-400' },
];

// Contract status
export const CONTRACT_STATUS = [
  { id: 'draft', name: 'Draft', color: 'bg-neutral-600', textColor: 'text-neutral-400' },
  { id: 'sent', name: 'Sent for Signature', color: 'bg-sky-600', textColor: 'text-sky-400' },
  { id: 'viewed', name: 'Viewed', color: 'bg-violet-600', textColor: 'text-violet-400' },
  { id: 'signed-creator', name: 'Signed by Creator', color: 'bg-amber-600', textColor: 'text-amber-400' },
  { id: 'signed-brand', name: 'Signed by Brand', color: 'bg-cyan-600', textColor: 'text-cyan-400' },
  { id: 'completed', name: 'Fully Executed', color: 'bg-lime-400', textColor: 'text-lime-400' },
  { id: 'expired', name: 'Expired', color: 'bg-red-600', textColor: 'text-red-400' },
];

// Application status
export const APPLICATION_STATUS = [
  { id: 'pending', name: 'Pending', color: 'bg-amber-600', textColor: 'text-amber-400' },
  { id: 'reviewing', name: 'Reviewing', color: 'bg-sky-600', textColor: 'text-sky-400' },
  { id: 'accepted', name: 'Accepted', color: 'bg-lime-400', textColor: 'text-lime-400' },
  { id: 'rejected', name: 'Rejected', color: 'bg-red-600', textColor: 'text-red-400' },
];

// Contract templates
export const CONTRACT_TEMPLATES = [
  {
    id: 'sponsored-video',
    name: 'Sponsored Video Agreement',
    description: 'Standard agreement for sponsored video content',
    sections: [
      { title: 'Parties', content: 'This Sponsorship Agreement ("Agreement") is entered into between [BRAND_NAME] ("Brand") and [CREATOR_NAME] ("Creator").' },
      { title: 'Scope of Work', content: 'Creator agrees to produce and publish [DELIVERABLE_COUNT] [DELIVERABLE_TYPE] featuring Brand\'s products/services on [PLATFORM].' },
      { title: 'Compensation', content: 'Brand agrees to pay Creator [PAYMENT_AMOUNT] for the services described herein. Payment will be made within [PAYMENT_TERMS] days of deliverable completion.' },
      { title: 'Content Guidelines', content: 'All content must be original, comply with FTC disclosure requirements, and follow platform community guidelines. Creator will include appropriate sponsorship disclosures.' },
      { title: 'Usage Rights', content: 'Brand is granted [USAGE_RIGHTS] rights to use the content for [USAGE_DURATION]. Creator retains ownership of the original content.' },
      { title: 'Exclusivity', content: 'Creator agrees to [EXCLUSIVITY_TERMS] for [EXCLUSIVITY_DURATION] from the publication date.' },
      { title: 'Deadlines', content: 'Creator will deliver draft content by [DRAFT_DEADLINE] and publish final content by [PUBLISH_DEADLINE].' },
      { title: 'Termination', content: 'Either party may terminate this agreement with [NOTICE_PERIOD] days written notice. In case of termination, Creator will be compensated for work completed.' },
    ]
  },
  {
    id: 'brand-ambassador',
    name: 'Brand Ambassador Agreement',
    description: 'Long-term ambassador partnership contract',
    sections: [
      { title: 'Parties', content: 'This Brand Ambassador Agreement is between [BRAND_NAME] ("Brand") and [CREATOR_NAME] ("Ambassador").' },
      { title: 'Term', content: 'This agreement is effective from [START_DATE] to [END_DATE], with option to renew upon mutual agreement.' },
      { title: 'Ambassador Duties', content: 'Ambassador agrees to: represent Brand positively, create [MONTHLY_CONTENT_COUNT] pieces of content monthly, attend [EVENT_COUNT] events per quarter, and maintain active engagement with Brand.' },
      { title: 'Compensation', content: 'Brand agrees to pay Ambassador [MONTHLY_RETAINER] monthly, plus [COMMISSION_RATE]% commission on attributed sales using code [PROMO_CODE].' },
      { title: 'Products & Perks', content: 'Ambassador will receive [PRODUCT_ALLOWANCE] in products quarterly and [ADDITIONAL_PERKS].' },
      { title: 'Exclusivity', content: 'Ambassador agrees not to promote competing brands in the [INDUSTRY] space during the term of this agreement.' },
      { title: 'Content Approval', content: 'Brand has [APPROVAL_WINDOW] hours to review and approve content before publication.' },
      { title: 'Confidentiality', content: 'Ambassador agrees to keep all non-public Brand information confidential during and after the term.' },
    ]
  },
  {
    id: 'affiliate',
    name: 'Affiliate Partnership Agreement',
    description: 'Commission-based affiliate marketing contract',
    sections: [
      { title: 'Parties', content: 'This Affiliate Agreement is between [BRAND_NAME] ("Merchant") and [CREATOR_NAME] ("Affiliate").' },
      { title: 'Program Description', content: 'Affiliate will promote Merchant\'s products/services using unique tracking links and codes.' },
      { title: 'Commission Structure', content: 'Affiliate will earn [COMMISSION_RATE]% commission on all qualified sales. Commission is calculated on [NET_OR_GROSS] sale amount.' },
      { title: 'Payment Terms', content: 'Commissions are paid [PAYMENT_FREQUENCY] via [PAYMENT_METHOD]. Minimum payout threshold is [MIN_PAYOUT].' },
      { title: 'Tracking & Attribution', content: 'Sales are tracked via [TRACKING_METHOD]. Attribution window is [COOKIE_DURATION] days.' },
      { title: 'Prohibited Activities', content: 'Affiliate may not: use paid ads without approval, create misleading claims, or spam potential customers.' },
      { title: 'Termination', content: 'Either party may terminate with [NOTICE_PERIOD] days notice. Pending commissions will be paid within [FINAL_PAYMENT] days.' },
    ]
  },
  {
    id: 'custom',
    name: 'Custom Contract',
    description: 'Build a contract from scratch',
    sections: []
  }
];

// Subscription tier limits
export const TIER_LIMITS = {
  free: { deals: 3, mediaKits: 1, name: 'Free', price: 0 },
  starter: { deals: 10, mediaKits: 3, name: 'Starter', price: 15 },
  pro: { deals: 30, mediaKits: 10, name: 'Pro', price: 40 },
  business: { deals: 100, mediaKits: -1, name: 'Business', price: 100 },
  enterprise: { deals: -1, mediaKits: -1, name: 'Enterprise', price: 200 },
};

// Storage key for localStorage
export const STORAGE_KEY = 'sponsy_data_v2';

// Default media kit
export const DEFAULT_MEDIA_KIT = {
  displayName: '',
  bio: '',
  niche: '',
  location: '',
  email: '',
  website: '',
  platforms: [],
  rateCard: [],
  portfolio: [],
  isPublic: false,
};

// Default profile
export const DEFAULT_PROFILE = {
  name: '',
  email: '',
  avatar: '',
};

// Navigation items
export const NAV_ITEMS = [
  { id: 'pipeline', label: 'Pipeline', badge: 'activeDeals' },
  { id: 'brands', label: 'Brands', badge: null },
  { id: 'contracts', label: 'Contracts', badge: 'pendingContracts' },
  { id: 'marketplace', label: 'Marketplace', badge: 'openOpportunities' },
  { id: 'invites', label: 'Invites', badge: 'pendingInvites' },
  { id: 'invoices', label: 'Invoices', badge: 'pendingInvoices' },
  { id: 'mediakit', label: 'Media Kit', badge: null },
  { id: 'analytics', label: 'Analytics', badge: null },
];
